import {
  ArrowPreviousIcon,
  AuthIcon,
  CollectionIcon,
  CorrectAnswerIcon,
  FilterIcon,
  LTIIcon,
  MembersIcon,
  PluginIcon,
  SecureIcon,
} from '@coursera/cds-icons';

import type { AdminSideNavConfigDefs } from 'bundles/enterprise-admin-navigation/components/side-nav/config/sideNavConfigDefs';
import {
  HOME_PATH,
  SETTINGS_CURATION_PATH,
  SETTINGS_CUSTOMIZE_ORG_PATH,
  SETTINGS_DOMAIN_SECURITY_PATH,
  SETTINGS_INTEGRATION_API_KEYS_PATH,
  SETTINGS_INTEGRATION_CORNERSTONE_PATH,
  SETTINGS_INTEGRATION_DEGREED_PATH,
  SETTINGS_INTEGRATION_MCP_PATH,
  SETTINGS_INTEGRATION_WORKDAY_PATH,
  SETTINGS_INTEGRATION_XAPI_PATH,
  SETTINGS_INTEGRITY_GRADING_PATH,
  SETTINGS_LTI_ADDITIONAL_SETTINGS_PATH,
  SETTINGS_LTI_TOOL_CONFIGURATIONS_PATH,
  SETTINGS_SCIM_GROUP_MAPPING_PATH,
  SETTINGS_SCIM_SETUP_PATH,
  SETTINGS_SSO_CONFIGURATION_PATH,
  SETTINGS_SSO_SETTINGS_PATH,
} from 'bundles/enterprise-admin-navigation/utils/pathConstants';

import _t from 'i18n!nls/enterprise-admin-navigation';

/**
 * The single source of truth for the admin settings side-nav items.
 *
 * Reproduces the settings left-nav structure: the items rendered between the
 * Coursera logo and the bottom slot when the user is inside `${SETTINGS_PATH}`.
 * Uses the same `AdminSideNavConfigDefs` shape as `sideNavConfigDefs`.
 *
 * Exported as a constant to prevent array recreation on every hook invocation.
 * Labels are resolved at render time via the `getLabel` predicates.
 */
export const settingsSideNavConfigDefs: AdminSideNavConfigDefs = {
  top: [
    {
      id: 'SETTINGS_CUSTOMIZE_ORG',
      getLabel: () => _t('Customize Organization'),
      icon: FilterIcon,
      href: SETTINGS_CUSTOMIZE_ORG_PATH,
    },
    {
      id: 'SETTINGS_CURATION',
      getLabel: () => _t('Curation'),
      icon: CollectionIcon,
      href: SETTINGS_CURATION_PATH,
      shouldShow: (runtimeContext) => runtimeContext.enablePersonalizedReordering,
    },
    {
      id: 'SETTINGS_INTEGRATION',
      getLabel: () => _t('Integrations'),
      icon: PluginIcon,
      children: [
        {
          id: 'SETTINGS_INTEGRATION_API_KEYS',
          getLabel: () => _t('API keys'),
          href: SETTINGS_INTEGRATION_API_KEYS_PATH,
        },
        {
          id: 'SETTINGS_INTEGRATION_WORKDAY',
          getLabel: () => _t('Workday'),
          href: SETTINGS_INTEGRATION_WORKDAY_PATH,
        },
        {
          id: 'SETTINGS_INTEGRATION_DEGREED',
          getLabel: () => _t('Degreed'),
          href: SETTINGS_INTEGRATION_DEGREED_PATH,
        },
        {
          id: 'SETTINGS_INTEGRATION_CORNERSTONE',
          getLabel: () => _t('Cornerstone'),
          href: SETTINGS_INTEGRATION_CORNERSTONE_PATH,
        },
        {
          id: 'SETTINGS_INTEGRATION_XAPI',
          getLabel: () => _t('xAPI'),
          href: SETTINGS_INTEGRATION_XAPI_PATH,
        },
        {
          id: 'SETTINGS_INTEGRATION_MCP',
          getLabel: () => _t('MCP'),
          href: SETTINGS_INTEGRATION_MCP_PATH,
        },
      ],
    },
    {
      id: 'SETTINGS_INTEGRITY_GRADING',
      getLabel: () => _t('Integrity and grading'),
      icon: CorrectAnswerIcon,
      href: SETTINGS_INTEGRITY_GRADING_PATH,
    },
    {
      id: 'SETTINGS_LTI',
      getLabel: () => _t('LTI'),
      icon: LTIIcon,
      children: [
        {
          id: 'SETTINGS_LTI_TOOL_CONFIGURATIONS',
          getLabel: () => _t('Tool configuration'),
          href: SETTINGS_LTI_TOOL_CONFIGURATIONS_PATH,
        },
        {
          id: 'SETTINGS_LTI_ADDITIONAL_SETTINGS',
          getLabel: () => _t('Additional settings'),
          href: SETTINGS_LTI_ADDITIONAL_SETTINGS_PATH,
        },
      ],
    },
    {
      id: 'SETTINGS_SCIM',
      getLabel: () => _t('SCIM'),
      icon: MembersIcon,
      children: [
        {
          id: 'SETTINGS_SCIM_SETUP',
          getLabel: () => _t('Setup'),
          href: SETTINGS_SCIM_SETUP_PATH,
        },
        {
          id: 'SETTINGS_SCIM_GROUP_MAPPING',
          getLabel: () => _t('Group mapping'),
          href: SETTINGS_SCIM_GROUP_MAPPING_PATH,
        },
      ],
    },
    {
      id: 'SETTINGS_DOMAIN_SECURITY',
      getLabel: () => _t('Domain security'),
      icon: SecureIcon,
      href: SETTINGS_DOMAIN_SECURITY_PATH,
    },
    {
      id: 'SETTINGS_SSO',
      getLabel: () => _t('SSO'),
      icon: AuthIcon,
      children: [
        {
          id: 'SETTINGS_SSO_SETTINGS',
          getLabel: () => _t('Configure SSO'),
          href: SETTINGS_SSO_SETTINGS_PATH,
        },
        {
          id: 'SETTINGS_SSO_CONFIGURATION',
          getLabel: () => _t('Additional settings'),
          href: SETTINGS_SSO_CONFIGURATION_PATH,
          shouldShow: (runtimeContext) =>
            runtimeContext.isUnifiedSsoEnabled && runtimeContext.thirdPartyOrganization.loginMethod === 'SAML',
        },
      ],
    },
  ],
  bottom: [
    {
      id: 'BACK_TO_HOME',
      getLabel: () => _t('Back to Home'),
      icon: ArrowPreviousIcon,
      href: HOME_PATH,
    },
  ],
};
